import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  FolderKanban, 
  BarChart3, 
  Clock, 
  Target,
  ArrowRight,
  FileEdit,
  Sparkles,
  Loader2,
  Plus,
} from "lucide-react";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useProjects } from "@/hooks/useProjects";
import { useProfile } from "@/hooks/useProfile";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { CreditBalanceWidget } from "@/components/dashboard/CreditBalanceWidget";
import { toast } from "@/hooks/use-toast";

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { stats, loading: statsLoading } = useDashboardStats();
  const { projects, loading: projectsLoading } = useProjects();
  const { profile } = useProfile();
  const { refreshSubscription, refreshCredits } = useSubscription();

  useEffect(() => {
    const checkoutStatus = searchParams.get("checkout");
    if (checkoutStatus === "success") {
      searchParams.delete("checkout");
      setSearchParams(searchParams, { replace: true });
      
      toast({
        title: "Subscription activated!",
        description: "Your plan has been upgraded. Refreshing your account...",
      });
      
      const refreshAfterCheckout = async () => {
        await new Promise(resolve => setTimeout(resolve, 2000));
        await Promise.all([refreshSubscription(), refreshCredits()]);
        toast({
          title: "Account updated",
          description: "Your new plan and credits are now active.",
        });
      };
      refreshAfterCheckout();
    }
  }, [searchParams, setSearchParams, refreshSubscription, refreshCredits]);

  const recentProjects = projects.slice(0, 3);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getClarityColor = (score: number) => {
    if (score >= 80) return "text-reqqy-success";
    if (score >= 60) return "text-reqqy-warning";
    return "text-destructive";
  };

  const greeting = profile?.full_name 
    ? `Welcome back, ${profile.full_name.split(' ')[0]}!`
    : "Welcome back!";

  return (
    <div className="min-h-screen p-6 md:p-8 lg:p-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold mb-1">Dashboard</h1>
          <p className="text-muted-foreground">{greeting} Start evaluating or generating requirements.</p>
        </div>
      </div>

      {/* Primary CTAs */}
      <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-8">
        <Card className="glass card-hover overflow-hidden group">
          <CardContent className="p-4 sm:p-6 md:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0 group-hover:glow-soft transition-all">
                <FileEdit className="w-6 h-6 sm:w-7 sm:h-7 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold mb-2">Evaluate requirements</h2>
                <p className="text-sm sm:text-base text-muted-foreground mb-4">
                  Paste existing requirements and get clarity analysis with improvement suggestions.
                </p>
                <Button asChild className="gradient-primary text-primary-foreground hover:opacity-90 w-full sm:w-auto">
                  <Link to="/dashboard/new-run?module=evaluation">
                    Start Evaluation
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass card-hover overflow-hidden group">
          <CardContent className="p-4 sm:p-6 md:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold mb-2">Generate requirements</h2>
                <p className="text-sm sm:text-base text-muted-foreground mb-4">
                  Describe what you need and let Reqqy generate clear requirements.
                </p>
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/dashboard/new-run?module=generation">
                    Start Generating
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <CreditBalanceWidget />
        <Card className="glass">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-primary" />
              </div>
              {statsLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>
            <div className="text-2xl font-display font-bold mb-1">
              {statsLoading ? "-" : stats.runsThisMonth}
            </div>
            <div className="text-sm text-muted-foreground">Runs this month</div>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-accent" />
              </div>
              {statsLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>
            <div className={`text-2xl font-display font-bold mb-1 ${stats.avgClarityScore > 0 ? getClarityColor(stats.avgClarityScore) : ""}`}>
              {statsLoading ? "-" : stats.avgClarityScore > 0 ? `${stats.avgClarityScore}%` : "—"}
            </div>
            <div className="text-sm text-muted-foreground">Avg. clarity score</div>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-reqqy-success/10 flex items-center justify-center">
                <FolderKanban className="w-5 h-5 text-reqqy-success" />
              </div>
              {statsLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>
            <div className="text-2xl font-display font-bold mb-1">
              {statsLoading ? "-" : stats.totalProjects}
            </div>
            <div className="text-sm text-muted-foreground">Active projects</div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Projects */}
      <Card className="glass">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Recent Projects</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/dashboard/projects">
              View all
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {projectsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : recentProjects.length === 0 ? (
            <div className="text-center py-12">
              <FolderKanban className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No projects yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first project to organize your requirements
              </p>
              <Button asChild className="gradient-primary text-primary-foreground">
                <Link to="/dashboard/projects">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Project
                </Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {recentProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/dashboard/projects/${project.id}`}
                  className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-background/50 hover:bg-background transition-colors group"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FolderKanban className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm sm:text-base font-semibold truncate group-hover:text-primary transition-colors">
                        {project.name}
                      </h3>
                      {project.avg_clarity_score !== undefined && (
                        <span className={`text-xs sm:hidden font-medium ${getClarityColor(project.avg_clarity_score)}`}>
                          {project.avg_clarity_score}%
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground truncate">
                      {project.description || "No description"}
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center gap-4 lg:gap-6 text-sm text-muted-foreground flex-shrink-0">
                    {project.avg_clarity_score !== undefined && (
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        <span className={getClarityColor(project.avg_clarity_score)}>
                          {project.avg_clarity_score}%
                        </span>
                      </div>
                    )}
                    {project.last_run_at && (
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <Clock className="w-4 h-4" />
                        {formatDate(project.last_run_at)}
                      </div>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
